import { Phone } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { RepescagemLead } from './types';
import { formatCurrency, formatDate, formatDateTime } from './types';

interface RepescagemCardProps {
  lead: RepescagemLead;
  onClick: (lead: RepescagemLead) => void;
}

export function RepescagemCard({ lead, onClick }: RepescagemCardProps) {
  const firstContactName = lead.contactNames[0] ?? null;
  const firstPhone = lead.contactPhones[0] ?? null;
  const isRejected = lead.quoteStatus === 'ORCAMENTO_RECUSADO';
  const attemptsExhausted = lead.attemptCount >= lead.maxAttempts;

  return (
    <button
      type="button"
      onClick={() => onClick(lead)}
      className={cn(
        'w-full text-left rounded-lg border bg-card p-3 shadow-sm',
        'hover:shadow-md hover:border-primary/30 transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
      )}
    >
      {/* Header: quote number + status badge */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="text-xs font-bold text-primary">#{lead.quoteNumber}</span>
        <span
          className={cn(
            'inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full border',
            isRejected
              ? 'bg-red-500/10 text-red-500 border-red-500/20'
              : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
          )}
        >
          {lead.quoteStatusLabel}
        </span>
      </div>

      {/* Client name */}
      <p className="text-sm font-medium text-foreground leading-tight truncate mb-1">
        {lead.clientName}
      </p>

      {/* Contact name */}
      {firstContactName && (
        <p className="text-xs text-muted-foreground truncate mb-1.5">
          {firstContactName}
        </p>
      )}

      {/* First phone */}
      {firstPhone && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1.5">
          <Phone className="h-3 w-3 flex-shrink-0" />
          <span>{firstPhone}</span>
        </div>
      )}

      {/* Footer: attempts + date + value */}
      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-border/50">
        {/* Attempt count */}
        {lead.stage !== 'LEAD' && (
          <span
            className={cn(
              'text-xs font-semibold',
              attemptsExhausted ? 'text-red-500' : 'text-muted-foreground'
            )}
          >
            {lead.attemptCount}/{lead.maxAttempts} tent.
          </span>
        )}

        {/* Last attempt date */}
        {lead.lastAttemptAt ? (
          <span className="text-xs text-muted-foreground ml-auto">
            {formatDateTime(lead.lastAttemptAt)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground ml-auto">
            {formatDate(lead.quoteDate)}
          </span>
        )}
      </div>

      {/* Total value */}
      {lead.totalValue !== null && (
        <p className="text-xs font-semibold text-foreground mt-1">
          {formatCurrency(lead.totalValue)}
        </p>
      )}
    </button>
  );
}
